import { css } from '@compiled/react';
import { useIntl } from 'react-intl';

import BugIcon from '@atlaskit/icon/core/bug';
import CheckCircleIcon from '@atlaskit/icon/core/check-circle';
import ClockIcon from '@atlaskit/icon/core/clock';
import ExclamationSquareIcon from '@atlaskit/icon/core/exclamation-square';
import LightbulbIcon from '@atlaskit/icon/core/lightbulb';
import PriorityHighIcon from '@atlaskit/icon/core/priority-high';
import PriorityHighestIcon from '@atlaskit/icon/core/priority-highest';
import PriorityLowIcon from '@atlaskit/icon/core/priority-low';
import PriorityLowestIcon from '@atlaskit/icon/core/priority-lowest';
import PriorityMediumIcon from '@atlaskit/icon/core/priority-medium';
import ToolsIcon from '@atlaskit/icon/core/tools';
import VideoPauseIcon from '@atlaskit/icon/core/video-pause';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';

import {
	ENGINEERING_PRIORITY_LABELS,
	type EngineeringQueueRow,
	type EngineeringRequestType,
} from './jsm-playground-engineering-data';
import { engineeringMessages as messages } from './jsm-playground-engineering-messages';

const requestTypeConfig = {
	bug: BugIcon,
	feature: LightbulbIcon,
	support: ToolsIcon,
	system: ExclamationSquareIcon,
} as const;

export const EngineeringRequestTypeIcon = ({
	size = 'small',
	type,
}: {
	size?: 'small' | 'medium';
	type: EngineeringRequestType;
}) => {
	const Icon = requestTypeConfig[type];
	return (
		<span
			css={[inlineFlexCenterStyles, requestTypeStyles]}
			data-testid={`engineering-request-type-${type}`}
		>
			<Icon color={token('color.icon.subtle')} label="" size={size} />
		</span>
	);
};

const priorityConfig = {
	Highest: { icon: PriorityHighestIcon, color: token('color.icon.danger') },
	High: { icon: PriorityHighIcon, color: token('color.icon.danger') },
	Medium: { icon: PriorityMediumIcon, color: token('color.icon.warning') },
	Low: { icon: PriorityLowIcon, color: token('color.icon.information') },
	Lowest: { icon: PriorityLowestIcon, color: token('color.icon.information') },
} as const;

export const EngineeringPriorityIcon = ({
	priority,
}: {
	priority: EngineeringQueueRow['priority'];
}) => {
	const intl = useIntl();
	const config = priorityConfig[priority];
	const Icon = config.icon;
	return (
		<span
			aria-label={intl.formatMessage(messages.priorityIconLabel, {
				priority: intl.formatMessage(ENGINEERING_PRIORITY_LABELS[priority]),
			})}
			css={inlineFlexCenterStyles}
			role="img"
		>
			<Icon color={config.color} label="" size="small" />
		</span>
	);
};

const inlineFlexCenterStyles = css({
	display: 'inline-flex',
	alignItems: 'center',
	justifyContent: 'center',
});

export const EngineeringSlaTag = ({
	isLoading = false,
	row,
}: {
	isLoading?: boolean;
	row: EngineeringQueueRow;
}) => {
	const intl = useIntl();
	const isBreached = row.slaTone === 'breached';
	const iconColor = isBreached
		? token('color.icon.inverse')
		: row.slaTone === 'met'
			? token('color.icon.success')
			: token('color.icon.subtle');
	const Icon =
		row.slaTone === 'met' ? CheckCircleIcon : row.slaTone === 'paused' ? VideoPauseIcon : ClockIcon;

	return (
		<span css={[slaTagStyles, isBreached && breachedSlaTagStyles]}>
			<Icon color={iconColor} label="" size="small" />
			{isLoading ? (
				<Skeleton borderRadius={token('radius.xsmall')} height={10} width={104} />
			) : (
				intl.formatMessage(row.sla)
			)}
		</span>
	);
};

const requestTypeStyles = css({
	flexShrink: 0,
});

const slaTagStyles = css({
	display: 'inline-flex',
	height: token('space.300'),
	alignItems: 'center',
	gap: token('space.050'),
	paddingInline: token('space.100'),
	borderRadius: token('radius.full'),
	backgroundColor: token('color.background.accent.gray.subtlest'),
	color: token('color.text.subtle'),
	font: token('font.body'),
	fontWeight: token('font.weight.medium'),
	whiteSpace: 'nowrap',
});

const breachedSlaTagStyles = css({
	backgroundColor: token('color.background.danger.bold'),
	color: token('color.text.inverse'),
});
